/**
 * Final scoring (rulebook p.10) and the winner. Pure functions over a state;
 * `finishGame` applies them and closes the game.
 */
import { MANIFESTS } from './manifests.js';
const FORUM_ICON = {
    bread: 'bread',
    helmet: 'games',
    flame: 'religion',
};
/** Best VP for `n` identical construction tiles (sets of 4 beat sets of 3). */
export function setValue(n) {
    const four = MANIFESTS.scoring.constructionSets['4'];
    const three = MANIFESTS.scoring.constructionSets['3'];
    let best = 0;
    for (let a = 0; a * 4 <= n; a++) {
        const b = Math.floor((n - a * 4) / 3);
        best = Math.max(best, a * four + b * three);
    }
    return best;
}
/** Construction-set VP with `wilds` wild tiles distributed where they help most. */
export function constructionSetVp(counts, wilds) {
    // dp over icons (plus one "new icon" so wilds alone can form a set): best total using w wilds so far
    let dp = new Array(wilds + 1).fill(0);
    for (const c of [...counts, 0]) {
        const next = new Array(wilds + 1).fill(0);
        for (let used = 0; used <= wilds; used++) {
            for (let w = 0; used + w <= wilds; w++) {
                next[used + w] = Math.max(next[used + w] ?? 0, (dp[used] ?? 0) + setValue(c + w));
            }
        }
        dp = next;
    }
    return Math.max(...dp);
}
export function bonusTileVp(state, player) {
    let total = 0;
    let halfWorkers = 0;
    const yellowCount = player.bonusTiles.filter((t) => t.side === 'yellow').length;
    const workersOnSites = state.board.sites.reduce((n, s) => n + s.workers.filter((id) => id === player.id).length, 0);
    const legionnairesOut = Object.values(state.board.provinces).reduce((n, p) => n + p.legionnaires.filter((id) => id === player.id).length, 0);
    for (const tile of player.bonusTiles) {
        const rate = tile.side === 'yellow' ? tile.yellow : tile.grey;
        switch (tile.kind) {
            case 'demand-icon': {
                const icon = tile.icon ? FORUM_ICON[tile.icon] : null;
                if (icon && player.forumTiles.some((f) => f.kind === 'demand' && f.icon === icon))
                    total += rate;
                break;
            }
            case 'per-display-card': {
                // R-032: the tile names a commodity; only cards of that type in the display count.
                const wanted = tile.commodities ?? [];
                total += rate * player.display.filter((c) => wanted.includes(c.type)).length;
                break;
            }
            case 'per-worker-on-site':
                if (Number.isInteger(rate))
                    total += rate * workersOnSites;
                else
                    halfWorkers += workersOnSites; // grey ½ per worker, rounded up at the end
                break;
            case 'per-legionnaire-in-province':
                total += rate * legionnairesOut;
                break;
            case 'per-yellow-tile':
                total += rate * yellowCount;
                break;
        }
    }
    return total + Math.ceil(halfWorkers / 2);
}
export function scorePlayer(state, player) {
    const S = MANIFESTS.scoring.endGame;
    const counts = new Map();
    for (const t of player.constructionTiles)
        counts.set(t.icon, (counts.get(t.icon) ?? 0) + 1);
    const wilds = player.forumTiles.filter((t) => t.kind === 'wild-construction').length;
    const b = {
        cardsInHand: S.perCardInHand * player.hand.length,
        workersInCamp: S.perWorkerInCamp * player.workers,
        legionnairesInCamp: S.perLegionnaireInCamp * player.legionnaires,
        trajanTilesOnMat: S.perTrajanTileOnMat * player.trajanSlots.filter(Boolean).length,
        constructionSets: constructionSetVp([...counts.values()], wilds),
        bonusTiles: bonusTileVp(state, player),
        total: 0,
    };
    b.total =
        b.cardsInHand +
            b.workersInCamp +
            b.legionnairesInCamp +
            b.trajanTilesOnMat +
            b.constructionSets +
            b.bonusTiles;
    return b;
}
/** Winner(s): most VP; ties broken by senate position (R-030), shared if still tied. */
export function winners(players) {
    const sorted = [...players].sort((a, b) => b.vp - a.vp || b.senate - a.senate);
    const top = sorted[0];
    if (!top)
        return [];
    return sorted.filter((p) => p.vp === top.vp && p.senate === top.senate).map((p) => p.id);
}
export function finishGame(state, events) {
    const breakdowns = {};
    let s = state;
    for (const p of s.players) {
        const b = scorePlayer(s, p);
        breakdowns[String(p.id)] = b;
        events.push({ type: 'FinalScoring', player: p.id, breakdown: b });
    }
    s = {
        ...s,
        players: s.players.map((p) => ({ ...p, vp: p.vp + (breakdowns[String(p.id)]?.total ?? 0) })),
    };
    const w = winners(s.players);
    const scores = {};
    for (const p of s.players)
        scores[String(p.id)] = p.vp;
    events.push({ type: 'GameOver', winners: w, scores });
    return {
        ...s,
        winner: w,
        finalScores: breakdowns,
        phase: { kind: 'gameOver' },
        quarterEnd: null,
    };
}
//# sourceMappingURL=scoring.js.map